// ── Security Utilities ──────────────────────────────────────────────────
// Sanitization helpers to prevent credential leakage in exports and logs.

const CREDENTIAL_KEYS = new Set([
  'accessKeyId', 'secretAccessKey', 'sessionToken',
  'clientSecret', 'deviceCode',
  'anthropicApiKey', 'xaiApiKey',
  'x-api-key', 'X-Anthropic-Api-Key', 'X-Xai-Api-Key',
])

/**
 * Recursively strips credential-pattern keys from objects before export/save.
 * Returns a deep copy with all sensitive keys removed.
 */
export function sanitizeForExport<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj
  if (typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(sanitizeForExport) as T

  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (CREDENTIAL_KEYS.has(key)) continue
    result[key] = sanitizeForExport(value)
  }
  return result as T
}

/**
 * Strips potential credential patterns from error messages to prevent
 * accidental credential leakage in console output or error boundaries.
 */
export function sanitizeErrorMessage(message: string): string {
  // Strip AWS access key patterns (AKIA followed by 16 chars)
  let sanitized = message.replace(/AKIA[A-Z0-9]{16}/g, '[REDACTED_KEY]')
  // Strip anything that looks like a secret key (40+ char base64-ish)
  sanitized = sanitized.replace(/(?:secret|key|token|password)[\s:="']*[A-Za-z0-9+/=]{20,}/gi, '[REDACTED]')
  // Strip sk-ant- prefixed keys
  sanitized = sanitized.replace(/sk-ant-[A-Za-z0-9_-]+/g, '[REDACTED_KEY]')
  // Strip xai- prefixed keys
  sanitized = sanitized.replace(/xai-[A-Za-z0-9_-]+/g, '[REDACTED_KEY]')
  return sanitized
}
